import type { QueryClient } from '@tanstack/react-query';

const financialRoots = new Set([
  'deal-kpi-periods', 'deal-kpi-data', 'deal-kpi-summary', 'deal-kpi-summary-dimensions',
  'deal-input-financials', 'kpi-hierarchy-breakdown', 'deal-scenarios',
]);
const dealRoots = new Set([
  'deal-input-financial-overview', 'deal-latest-analysis', 'deal-analysis-status',
  'deal-analysis-latest', 'deal-analysis-outlier-count',
]);

/** Successful writes invalidate cached revisits, including writes outside the mounted tab.
 * Existing mutation owners refresh active views. Marking stale here starts no duplicate requests.
 */
export async function markMutationQueriesStale(client: QueryClient, endpoint: string, method: string, fundId: string | null): Promise<void> {
  if (method === 'GET' || method === 'HEAD' || !fundId) return;
  const path = endpoint.split('?')[0];
  const dealMatch = path.match(/^\/deals\/([^/]+)(?:$|\/(?:input|scenarios|kpis|documents)(?:\/|$))/);
  const sourceWrite = /^\/(?:import|googlesheets|excel|quickbooks|appfolio)\//.test(path);
  const investorWrite = /^\/investors(?:\/|$)/.test(path);
  const portfolioWrite = path === '/deals' || Boolean(dealMatch);
  if (!dealMatch && !sourceWrite && !investorWrite && !portfolioWrite) return;
  const dealId = dealMatch?.[1];
  await client.invalidateQueries({
    refetchType: 'none',
    predicate: ({ queryKey: key }) => {
      const root = String(key[0]);
      if (sourceWrite && root === 'data-connections') return key[1] === fundId;
      if (investorWrite && root === 'investors') return key[1] === fundId;
      if (portfolioWrite && root === 'deals') return key[1] === 'list' && key[2] === fundId;
      if (dealId && root === 'deal-detail') return key[1] === fundId && key[2] === dealId;
      if (!dealId && !sourceWrite) return false;
      if (financialRoots.has(root)) return key[1] === fundId && (!dealId || key[2] === dealId);
      if (dealRoots.has(root)) return !dealId || key[1] === dealId;
      return root === 'statement-dataset' && key[1] === fundId;
    },
  });
}
